import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { X } from "lucide-react";

import type { TestCase } from "@/api/tests-api";
import { ReadingRoom } from "@/components/viewer/ReadingRoom";
import { RoomViewer } from "@/components/viewer/RoomViewer";

/**
 * A library case, shown the way a candidate will see it: the reading
 * room, embedded, as both rounds now read a case (2026-10-07). The
 * "Round 1 / screening round" toggle this used to offer existed because
 * the two rounds had different viewers; they no longer do.
 *
 * The brief above the images is the same text the candidate is given,
 * which is the other half of the check: whoever assembled the case can
 * confirm the history reads correctly against the pictures before any
 * candidate is assigned it.
 *
 * The only additions are administrative — what the candidate never sees:
 * how many exams use it, and how many candidates already hold it.
 */
export function CasePreviewModal({
  testCase,
  onClose,
}: {
  testCase: TestCase | null;
  onClose: () => void;
}) {
  const [reading, setReading] = useState(false);

  if (!testCase) return null;

  return (
    <>
    {reading && (
      <ReadingRoom
        source={{ kind: "library", caseId: testCase.id }}
        testCase={testCase}
        onClose={() => setReading(false)}
      />
    )}
    <Dialog.Root
      // CLOSED while the reading room is open: an open Radix dialog
      // disables pointer events outside its own layer, so the room
      // would draw on top and take no clicks (the 2026-10-06
      // "Remove every image?" defect). It reopens when the room closes.
      open={!reading}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-cy="case-preview-modal"
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[92vh] w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-lg"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-5 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                {testCase.title}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                {[testCase.modality, testCase.body_part]
                  .filter(Boolean)
                  .join(" · ") || "No modality recorded"}
                {" · "}
                {testCase.instance_count} image
                {testCase.instance_count === 1 ? "" : "s"}
                {testCase.exam_count > 0 &&
                  ` · in ${testCase.exam_count} exam${
                    testCase.exam_count === 1 ? "" : "s"
                  }`}
                {testCase.assigned_count > 0 &&
                  ` · ${testCase.assigned_count} candidate${
                    testCase.assigned_count === 1 ? "" : "s"
                  } hold it`}
              </Dialog.Description>
            </div>
            <button
              type="button"
              data-cy="btn-open-reading-room"
              onClick={() => setReading(true)}
              title="Open this case full-screen in the reading room"
              className="sk-btn sk-sm ml-auto shrink-0"
            >
              Reading room
            </button>
            <Dialog.Close
              data-cy="btn-close-case-preview"
              aria-label="Close"
              className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {testCase.clinical_history ? (
              <div className="mb-4 rounded-lg border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
                  The brief, as the candidate reads it
                </p>
                <p className="mt-1 text-sm text-foreground">
                  {testCase.clinical_history}
                </p>
              </div>
            ) : (
              <p
                data-cy="case-preview-no-history"
                className="mb-4 text-xs text-status-urgent"
              >
                This case has no clinical history — a candidate will be shown
                the images with no context at all.
              </p>
            )}

            {/* 70vh, NOT 55. The image is what this modal is for, and
                55vh left the stage about 150px tall once the series list
                and the filmstrip had taken their share. */}
            <div className="flex h-[70vh] min-h-[420px] flex-col overflow-hidden rounded-lg">
              {/* FULL SCREEN CLOSES THIS DIALOG and opens the room on its
                  own. An open Radix modal disables pointer events outside
                  its layer and traps focus, so a full-screen layer in the
                  body would draw and take no clicks. */}
              <RoomViewer
                source={{ kind: "library", caseId: testCase.id }}
                testCase={testCase}
                explainFallback
                onFullScreen={() => setReading(true)}
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
    </>
  );
}
